import {
  createTRPCClient,
  httpBatchLink,
  TRPCClientError,
  type CreateTRPCClientOptions,
  type TRPCLink,
} from '@trpc/client'
import type { AnyRouter } from '@trpc/server'
import { observable } from '@trpc/server/observable'
import superjson from 'superjson'

// 注意：使用方应自行导入 AppRouter 类型。
// 例如：import type { AppRouter } from '@maxus/server/controllers/http/trpc'

export interface CreateMaxusTrpcClientOptions {
  // 任意非 auth.me 业务请求返回 401（UNAUTHORIZED）时触发，用于全局会话失效处理。
  onUnauthorized?: () => void
  // 每次请求前调用，返回要注入的 HTTP headers（如 Authorization: Bearer ...）。
  getHeaders?: () => Record<string, string> | Promise<Record<string, string>>
}

// 只暴露 baseUrl 入参，不在 common 里耦合任何埋点/监控依赖；调用方若需埋点可在外层自
// 行包装（fetch / link）。
export function createMaxusTrpcClient<TRouter extends AnyRouter = AnyRouter>(
  baseUrl: string,
  opts?: CreateMaxusTrpcClientOptions,
) {
  // 与原实现一致使用 `any` 绕开 transformer 泛型推导（superjson 类型与 TRouter 推导出的
  // transformer 类型不能在不知道 router 配置时静态匹配）。
  const linkOptions: any = {
    url: `${baseUrl}/trpc`,
    transformer: superjson,
    headers: opts?.getHeaders,
  }

  // 全局 401 拦截：仅当传入 onUnauthorized 时启用，命中非 auth.me 的 UNAUTHORIZED 时回调，
  // 错误照常透传（不吞错）。无 opts 时不挂此 link，行为与改动前完全一致（向后兼容）。
  const errorLink: TRPCLink<TRouter> =
    () =>
    ({ op, next }) =>
      observable(observer =>
        next(op).subscribe({
          next: value => observer.next(value),
          error: err => {
            if (err instanceof TRPCClientError && err.data?.code === 'UNAUTHORIZED' && op.path !== 'auth.me') {
              opts?.onUnauthorized?.()
            }
            observer.error(err)
          },
          complete: () => observer.complete(),
        }),
      )

  const options: CreateTRPCClientOptions<TRouter> = {
    links: opts?.onUnauthorized ? [errorLink, httpBatchLink(linkOptions)] : [httpBatchLink(linkOptions)],
  }

  return createTRPCClient<TRouter>(options)
}
